import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Chip, LinearProgress, TextField, Typography } from "@mui/material";
import { coverageRequest } from "services/coverage";
import useCoverageAnalysis from "./useCoverageAnalysis";
const { DEFAULT_COVERAGE_SETTINGS } = require("shared/coverageSettings");

const percent = (value) =>
  Number.isFinite(value)
    ? `${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`
    : "Não disponível";

function Highlight({ id, page }) {
  const element = useRef(null);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    let objectUrl;
    const observer = new IntersectionObserver(
      async (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        try {
          const response = await coverageRequest(`/${id}/pages/${page.page}`, {
            signal: controller.signal,
          });
          const blob = await response.blob();
          if (active) {
            objectUrl = URL.createObjectURL(blob);
            setUrl(objectUrl);
          }
        } catch (failure) {
          if (active) setError(failure.message);
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(element.current);
    return () => {
      active = false;
      observer.disconnect();
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, page.page]);
  return (
    <Box
      component="figure"
      ref={element}
      sx={{ m: 0, minWidth: 0, p: 1.5, bgcolor: "#f5f7f6", borderRadius: 2 }}
    >
      <Typography component="figcaption" variant="body2" fontWeight="bold" mb={1}>
        Página {page.page} · {percent(page.coveragePercent)}
      </Typography>
      {error ? (
        <Alert severity="warning">{error}</Alert>
      ) : url ? (
        <Box
          component="img"
          src={url}
          alt={`Página ${page.page}: áreas contabilizadas destacadas em laranja`}
          sx={{ width: "100%", maxHeight: 650, objectFit: "contain", display: "block" }}
        />
      ) : (
        <Box sx={{ minHeight: 180 }}>
          <Typography variant="caption">Carregando destaque…</Typography>
        </Box>
      )}
    </Box>
  );
}

function AnalysisTimer({ startedAt, finishedAt, status }) {
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    if (startedAt === null || finishedAt !== null) return undefined;
    setNow(performance.now());
    const interval = setInterval(() => setNow(performance.now()), 1000);
    return () => clearInterval(interval);
  }, [startedAt, finishedAt]);
  const seconds =
    startedAt === null ? 0 : Math.max(0, Math.floor(((finishedAt ?? now) - startedAt) / 1000));
  const duration = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(
    seconds % 60
  ).padStart(2, "0")}`;
  return (
    <Box sx={{ mb: 2, p: 1.5, border: "1px solid #dfe8e2", borderRadius: 2, bgcolor: "#f5f8f6" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          {finishedAt === null
            ? "Tempo decorrido"
            : status === "error"
            ? "Tempo até a interrupção"
            : "Tempo total"}{" "}
        </Typography>
        <Typography
          component="time"
          aria-label="Tempo da análise"
          dateTime={`PT${seconds}S`}
          variant="h6"
          sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
        >
          {duration}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Inclui envio do PDF, processamento e recebimento do resultado.
      </Typography>
    </Box>
  );
}

export function CoverageDetails({ analysis }) {
  const { state, timing, retry } = analysis;
  const [pageOrder, setPageOrder] = useState("page-asc");
  const pageList = useRef(null);
  const result = state.result;
  const rules = result?.rules || state.rules || DEFAULT_COVERAGE_SETTINGS;
  const limit = rules.colorCoveragePercent.toLocaleString("pt-BR");
  const minK = rules.minKPercent.toLocaleString("pt-BR");
  const sortedPages = useMemo(() => {
    const pages = [...(result?.pages || [])];
    return pages.sort((left, right) => {
      if (pageOrder === "page-desc") return right.page - left.page;
      if (pageOrder === "coverage-asc")
        return left.coveragePercent - right.coveragePercent || left.page - right.page;
      if (pageOrder === "coverage-desc")
        return right.coveragePercent - left.coveragePercent || left.page - right.page;
      return left.page - right.page;
    });
  }, [result?.pages, pageOrder]);
  return (
    <Box
      component="section"
      aria-label="Cobertura gráfica do miolo"
      sx={{
        mt: 3,
        p: { xs: 2, md: 3 },
        border: "1px solid #dfe8e2",
        borderRadius: 3,
        bgcolor: "white",
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="h5">Cobertura gráfica do miolo</Typography>
        {result && (
          <Button color="success" onClick={retry}>
            Analisar novamente
          </Button>
        )}
        <Chip
          size="small"
          label={
            state.rules || result
              ? `K ≥ ${minK}% · limite total ${limit}%`
              : state.status === "error"
              ? "Regra de cobertura indisponível"
              : "Carregando regra de cobertura…"
          }
        />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Imagens, fundos, formas e linhas; texto excluído por leitura do PDF e OCR.
      </Typography>
      <AnalysisTimer
        startedAt={timing.startedAt}
        finishedAt={timing.finishedAt}
        status={state.status}
      />
      <Box role="status" aria-live="polite">
        {["uploading", "running"].includes(state.status) && (
          <>
            <Typography variant="body2" mb={1}>
              {state.status === "uploading"
                ? "Enviando uma cópia temporária para análise…"
                : `Analisando página ${state.page || 1}${
                    state.total ? ` de ${state.total}` : ""
                  }… Você pode continuar conferindo o miolo.`}
            </Typography>
            <LinearProgress
              aria-label="Progresso da análise de cobertura"
              variant={state.total ? "determinate" : "indeterminate"}
              value={state.total ? Math.max(0, ((state.page - 1) / state.total) * 100) : 0}
            />
          </>
        )}
        {state.status === "error" && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={retry}>
                Tentar novamente
              </Button>
            }
          >
            Análise indisponível: {state.message} Nenhuma porcentagem ou classificação foi
            determinada. Você pode continuar preparando a obra, mas precisa concluir a análise antes
            de publicar.
          </Alert>
        )}
        {result && (
          <>
            {(result.genericProfile || result.spotColorSpaces?.length > 0) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {result.spotColorSpaces?.length > 0 && (
                  <>
                    Cores especiais convertidas automaticamente para CMYK pelas equivalências
                    definidas no PDF.{" "}
                  </>
                )}
                {result.genericProfile ? (
                  <>
                    Arquivo convertido para CMYK com perfil padrão. A cobertura é uma estimativa: o
                    resultado pode variar conforme o perfil de impressão da gráfica.
                  </>
                ) : (
                  <>A cobertura após a conversão é uma estimativa.</>
                )}
              </Alert>
            )}
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 1, mb: 1 }}>
              <Typography variant="h4" sx={{ fontVariantNumeric: "tabular-nums" }}>
                {percent(result.coveragePercent)}
              </Typography>
              <Typography variant="body2">de cobertura total estimada</Typography>
            </Box>
            <Alert severity={result.colored ? "warning" : "info"}>
              {result.colored
                ? "O livro será cobrado como colorido."
                : result.thresholdUncertain
                ? `A incerteza da exclusão de texto alcança o limite de ${limit}%. Solicite revisão técnica antes de confirmar a cobrança.`
                : `A cobertura calculada está abaixo do limite de ${limit}% para cobrança como colorido.`}
            </Alert>
          </>
        )}
      </Box>
      {result && (
        <>
          <Box component="details" sx={{ mt: 2 }}>
            <Typography component="summary" sx={{ cursor: "pointer", fontWeight: 600 }}>
              Como calculamos · perfil e precisão
            </Typography>
            <Typography variant="body2" mt={1}>
              {result.method}
            </Typography>
            <Typography variant="body2" mt={1}>
              {result.profile}.
            </Typography>
            <Typography variant="body2" mt={1}>
              {result.limitations}
            </Typography>
            <Typography variant="body2" mt={1}>
              Efeito da máscara de OCR: {percent(result.coveragePercent)} a{" "}
              {percent(result.upperPercent)}. A comparação com {limit}% usa os valores sem
              arredondamento.
            </Typography>
          </Box>
          <Box component="details" sx={{ mt: 2 }}>
            <Typography component="summary" sx={{ cursor: "pointer", fontWeight: 600 }}>
              Cobertura por página · {result.pages.length} páginas
            </Typography>
            <Box
              sx={{
                mt: 2,
                mb: 1.5,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <TextField
                select
                label="Ordenar páginas por"
                value={pageOrder}
                onChange={(event) => {
                  setPageOrder(event.target.value);
                  if (pageList.current) pageList.current.scrollTop = 0;
                }}
                SelectProps={{ native: true }}
                size="small"
                sx={{
                  width: { xs: "100%", sm: 300 },
                  "& .MuiInputBase-root": { minHeight: 44 },
                  "& .MuiNativeSelect-select": { height: "auto", py: 1.5 },
                }}
              >
                <option value="page-asc">Página · crescente (1 → última)</option>
                <option value="page-desc">Página · decrescente (última → 1)</option>
                <option value="coverage-asc">Cobertura (%) · menor para maior</option>
                <option value="coverage-desc">Cobertura (%) · maior para menor</option>
              </TextField>
              <Typography variant="caption" color="text.secondary">
                {result.pages.length} páginas · porcentagens sem arredondamento na ordenação
              </Typography>
            </Box>
            <Box ref={pageList} sx={{ maxHeight: 320, overflow: "auto", mt: 1 }}>
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.875rem",
                  "& th, & td": { p: 1, borderBottom: "1px solid #e2e9e4", textAlign: "left" },
                  "& thead th": {
                    position: "sticky",
                    top: 0,
                    bgcolor: "background.paper",
                    zIndex: 1,
                  },
                }}
              >
                <thead>
                  <tr>
                    <th scope="col">Página</th>
                    <th scope="col">Cobertura</th>
                    <th scope="col">Leitura</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPages.map((page) => (
                    <tr key={page.page}>
                      <th scope="row">{page.page}</th>
                      <td>{percent(page.coveragePercent)}</td>
                      <td>
                        {page.empty
                          ? `Sem pixels gráficos com K ≥ ${minK}%`
                          : page.ocrCharacters
                          ? "Texto reconhecido por OCR excluído"
                          : "Gráficos analisados"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          </Box>
          {result.colored && (
            <Box mt={3}>
              <Typography variant="h6">Páginas que contribuíram para a cobertura</Typography>
              <Typography variant="body2" mb={2}>
                O destaque laranja marca somente os pixels contabilizados, sobre a página original.
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                    xl: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {result.pages
                  .filter((page) => page.coveragePercent > 0)
                  .map((page) => (
                    <Highlight key={page.page} id={state.id} page={page} />
                  ))}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

function ManagedCoverageAnalysis({ file }) {
  const analysis = useCoverageAnalysis(file);
  return <CoverageDetails analysis={analysis} />;
}
export default function CoverageAnalysis({ file, analysis }) {
  return analysis ? (
    <CoverageDetails analysis={analysis} />
  ) : (
    <ManagedCoverageAnalysis file={file} />
  );
}
